import { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { ActivationStatus } from '@/entities/vfo/types';
import { Checkbox } from '@/features/vfo/components/Checkbox';
import { UserCell } from '@/features/vfo/components/UserCell';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { breakpoints, media } from '@/shared/components';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SelectMembersModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

export const SelectMembersModal: FC<SelectMembersModalProps> = ({
  onFinish,
  initialMembersProfileIds,
  hideProfileIds,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectMembers' });

  const [selectedMembers, setSelectedMembers] = useState<number[]>(initialMembersProfileIds ?? []);

  const { membersWithoutCurrentUser } = useMembers();
  const { closeModal } = useModalsStore();

  const handleToggle = async (profileId: number) => {
    setSelectedMembers((prev) =>
      prev.includes(profileId) ? prev.filter((m) => m !== profileId) : [...prev, profileId],
    );
  };

  const availableMembers = membersWithoutCurrentUser.filter(
    (item) =>
      item.activationStatus === ActivationStatus.ACTIVE &&
      !hideProfileIds?.includes(item.profileId ?? -1),
  );

  return (
    <ModalContainerV1 title={t('title')}>
      <ListContainer
        onClickCapture={(e) => {
          const clickedItem = (e.target as HTMLElement).closest<HTMLDivElement>('[data-profileId]');
          if (clickedItem) {
            const profileId = clickedItem.dataset.profileid;
            if (profileId) {
              handleToggle(Number(profileId));
            }
          }
        }}
      >
        {availableMembers.map((member) => {
          if (!member.profileId) return null;
          return (
            <ListItem key={member.profileId} data-profileId={member.profileId}>
              <Checkbox
                style={{
                  pointerEvents: 'none',
                }}
                checked={selectedMembers.includes(member.profileId)}
              />
              <UserCell clickable={false} member={member} />
            </ListItem>
          );
        })}
      </ListContainer>
      <Buttons>
        <Button
          size="middle"
          onClick={() => {
            closeModal();
            onFinish?.(selectedMembers);
          }}
        >
          {t('save')}
        </Button>
      </Buttons>
    </ModalContainerV1>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 24px;

  ${media.md}, ${breakpoints.sm} {
    padding: 0;
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  gap: 16px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    margin-top: auto;
  }
`;
